import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";

const content = {
  en: {
    title: "Terms of Service",
    updated: "Last updated: October 2026",
    sections: [
      { h: "1. Acceptance", p: "By accessing or using PINWIN (pinwin.social), you agree to be bound by these Terms. If you do not agree, please do not use the service." },
      { h: "2. The Service", p: "PINWIN is a social platform for recording wins, rankings and collections between friends and squads. The service is provided during its beta phase and features may change without notice." },
      { h: "3. Your Account", p: "You must be at least 13 years old to use PINWIN. You are responsible for maintaining the security of your account and for all activity that occurs under it." },
      { h: "4. Pins and Results", p: "You are responsible for the accuracy of the results you register. PINWIN does not verify the authenticity of pins — the squad confirmation system is the validation mechanism." },
      { h: "5. Acceptable Use", p: "You agree not to use PINWIN to harass, abuse or harm others; to impersonate another person; to post illegal content; to manipulate results fraudulently; or to interfere with the service's operation." },
      { h: "6. Content", p: "You retain ownership of content you post. By posting, you grant PINWIN a non-exclusive license to display that content within the service. You are responsible for what you share." },
      { h: "7. Handles", p: "By reserving a @handle, you claim that identifier for your account. PINWIN reserves the right to reclaim handles that are inactive, abusive, or in violation of these terms." },
      { h: "8. Privacy", p: "Your use of the service is also governed by our Privacy Policy. We collect only what is necessary to operate the service." },
      { h: "9. Termination", p: "PINWIN reserves the right to suspend or terminate accounts that violate community guidelines, including harassment, spam, or fraudulent activity." },
      { h: "10. Disclaimer", p: "PINWIN is provided without warranties of any kind. We are not liable for any damages arising from your use of the service." },
      { h: "11. Changes", p: "We may update these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms." },
      { h: "12. Contact", p: "For questions about these terms, contact us at hello@pinwin.social" },
    ],
  },
  es: {
    title: "Términos de Servicio",
    updated: "Última actualización: octubre de 2026",
    sections: [
      { h: "1. Aceptación", p: "Al acceder o utilizar PINWIN (pinwin.social), aceptas estos Términos. Si no estás de acuerdo, por favor no uses el servicio." },
      { h: "2. El Servicio", p: "PINWIN es una plataforma social para registrar victorias, rankings y colecciones entre amigos y squads. El servicio se proporciona en fase beta y las funciones pueden cambiar sin previo aviso." },
      { h: "3. Tu Cuenta", p: "Debes tener al menos 13 años para usar PINWIN. Eres responsable de mantener la seguridad de tu cuenta y de toda la actividad que ocurra en ella." },
      { h: "4. Pins y Resultados", p: "Eres responsable de la exactitud de los resultados que registras. PINWIN no verifica la autenticidad de los pins — el sistema de confirmación del squad es el mecanismo de validación." },
      { h: "5. Uso Aceptable", p: "Aceptas no usar PINWIN para acosar, abusar o dañar a otros; suplantar a otra persona; publicar contenido ilegal; manipular resultados de forma fraudulenta; o interferir con la operación del servicio." },
      { h: "6. Contenido", p: "Conservas la propiedad del contenido que publicas. Al publicar, otorgas a PINWIN una licencia no exclusiva para mostrar ese contenido dentro del servicio." },
      { h: "7. Handles", p: "Al reservar un @handle, reclamas ese identificador para tu cuenta. PINWIN se reserva el derecho de recuperar handles inactivos, abusivos o que violen estos términos." },
      { h: "8. Privacidad", p: "Tu uso del servicio también se rige por nuestra Política de Privacidad. Solo recopilamos lo necesario para operar el servicio." },
      { h: "9. Cancelación", p: "PINWIN se reserva el derecho de suspender o cancelar cuentas que violen las pautas de la comunidad, incluido el acoso, spam o actividad fraudulenta." },
      { h: "10. Descargo", p: "PINWIN se proporciona sin garantías de ningún tipo. No somos responsables de ningún daño derivado del uso del servicio." },
      { h: "11. Cambios", p: "Podemos actualizar estos términos en cualquier momento. El uso continuado del servicio tras los cambios constituye aceptación de los nuevos términos." },
      { h: "12. Contacto", p: "Para preguntas sobre estos términos, contáctanos en hello@pinwin.social" },
    ],
  },
  pt: {
    title: "Termos de Serviço",
    updated: "Última atualização: outubro de 2026",
    sections: [
      { h: "1. Aceitação", p: "Ao acessar ou usar o PINWIN (pinwin.social), você concorda com estes Termos. Se não concordar, por favor não use o serviço." },
      { h: "2. O Serviço", p: "PINWIN é uma plataforma social para registrar vitórias, rankings e coleções entre amigos e squads. O serviço é fornecido em fase beta e funcionalidades podem mudar sem aviso prévio." },
      { h: "3. Sua Conta", p: "Você deve ter pelo menos 13 anos para usar o PINWIN. Você é responsável pela segurança da sua conta e por toda atividade que ocorrer nela." },
      { h: "4. Pins e Resultados", p: "Você é responsável pela precisão dos resultados que registra. O PINWIN não verifica a autenticidade dos pins — o sistema de confirmação do squad é o mecanismo de validação." },
      { h: "5. Uso Aceitável", p: "Você concorda em não usar o PINWIN para assediar, abusar ou prejudicar outros; se passar por outra pessoa; publicar conteúdo ilegal; manipular resultados de forma fraudulenta; ou interferir na operação do serviço." },
      { h: "6. Conteúdo", p: "Você mantém a propriedade do conteúdo que publica. Ao publicar, você concede ao PINWIN uma licença não exclusiva para exibir esse conteúdo dentro do serviço." },
      { h: "7. Handles", p: "Ao reservar um @handle, você reivindica esse identificador para sua conta. O PINWIN se reserva o direito de recuperar handles inativos, abusivos ou que violem estes termos." },
      { h: "8. Privacidade", p: "Seu uso do serviço também é regido pela nossa Política de Privacidade. Coletamos apenas o necessário para operar o serviço." },
      { h: "9. Encerramento", p: "O PINWIN se reserva o direito de suspender ou encerrar contas que violem as diretrizes da comunidade, incluindo assédio, spam ou atividade fraudulenta." },
      { h: "10. Isenção", p: "O PINWIN é fornecido sem garantias de qualquer tipo. Não somos responsáveis por quaisquer danos decorrentes do uso do serviço." },
      { h: "11. Alterações", p: "Podemos atualizar estes termos a qualquer momento. O uso continuado do serviço após as alterações constitui aceitação dos novos termos." },
      { h: "12. Contato", p: "Para dúvidas sobre estes termos, entre em contato em hello@pinwin.social" },
    ],
  },
};

export default function Terms() {
  const { lang } = useLang();
  const c = content[lang] ?? content.en;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="mx-auto max-w-[720px] px-5 md:px-10 py-20 md:py-32">

        <Link
          to="/"
          className="inline-block font-mono-pw uppercase text-xs tracking-[0.2em] text-[#E6FF00] hover:opacity-70 transition-opacity mb-14"
        >
          ← PINWIN
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-anton uppercase text-5xl md:text-6xl leading-none mb-4">
            {c.title}
          </h1>
          <p className="font-mono-pw uppercase text-[10px] tracking-[0.25em] text-white/30 mb-16">
            {c.updated}
          </p>

          <div className="space-y-10">
            {c.sections.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 + i * 0.04 }}
                className="border-t border-white/10 pt-8"
              >
                <h2 className="font-mono-pw uppercase text-xs tracking-[0.2em] text-[#E6FF00] mb-3">
                  {s.h}
                </h2>
                <p className="font-body text-sm text-white/60 leading-relaxed">
                  {s.p}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </div>
  );
}
